import { fetcher } from '@chipinside/fetcher'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { isEmpty, isEqual } from 'lodash'
import { shallowEqual, useSelector } from 'react-redux'

import { CompanyType } from '#/store/ducks/company/type'
import { endpoint } from '#/store/services/endpoints'
import { sanitizeError } from '#/utils/SanitizeError'

type CompanyState = {
  loading: boolean
  company: CompanyType
}

const defaultState: CompanyState = {
  company: {} as CompanyType,
  loading: false,
}

export const loadCompany = createAsyncThunk(
  'farm/loadCompany',
  async ({ companySlug }: { companySlug: string }, thunkAPI) => {
    if (!isEmpty(companySlug)) {
      try {
        const controller = endpoint.company.self({ company: companySlug })
        // @ts-ignore
        const { data } = await fetcher<CompanyType>({ controller })

        return { data }
      } catch (error: any) {
        return thunkAPI.rejectWithValue({ error: sanitizeError(error) })
      }
    }

    return thunkAPI.rejectWithValue({
      message: 'load farm info not handled',
      slug: companySlug,
    })
  },
)

export const companySlice = createSlice({
  extraReducers: builder => {
    builder
      .addCase(loadCompany.pending, state => {
        state.loading = true
      })
      .addCase(loadCompany.rejected, (state, { payload }: any) => {
        state.loading = false
        throw payload.error
      })
      .addCase(loadCompany.fulfilled, (state, { payload }) => {
        state.company = payload?.data
        state.loading = false
      })
  },
  initialState: defaultState,
  name: 'company',
  reducers: {
    resetCompany(state) {
      state.company = {} as CompanyType
    },
  },
  selectors: {
    getCompany: state => state.company,
    getCompanyName: state => state.company.name ?? null,
    getCompanySlug: state => state.company.slug ?? null,
  },
})

export const {
  actions: { resetCompany },
  selectors: { getCompany, getCompanyName, getCompanySlug },
} = companySlice

export const useCompany = () => useSelector(getCompany, isEqual)
export const useCompanySlug = () => useSelector(getCompanySlug, shallowEqual)
export const useCompanyName = () => useSelector(getCompanyName, shallowEqual)

export default companySlice.reducer
